import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDateDuration(dateString: string) {
  // The backend returns dates without timezone info, so append 'Z' to treat as UTC
  // Format: "2025-12-03T15:57:53.063694" -> "2025-12-03T15:57:53.063694Z"
  const dateWithTz = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
  const date = new Date(dateWithTz);

  // Check if date is valid
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Handle future dates or very recent (less than 30 seconds)
  if (diffSecs < 30) {
    return "Just now";
  }

  if (diffMins < 1) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
}

export function formatDetailedDate(dateString: string | null | undefined): string {
  if (!dateString) return "-";

  try {
    // The backend returns dates without timezone info, so append 'Z' to treat as UTC
    const dateWithTz = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
    const date = new Date(dateWithTz);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "-";
  }
}

export function formatFileType(mimeType: string | null, filename?: string): string {
  // Helper function to get type from filename extension
  const getTypeFromFilename = (filename: string): string | null => {
    const extension = filename.split(".").pop()?.toLowerCase();
    if (!extension) return null;

    const extensionMap: Record<string, string> = {
      // Documents
      pdf: "PDF",
      doc: "Word Document",
      docx: "Word Document",
      txt: "Text Document",
      rtf: "Rich Text Document",
      odt: "OpenDocument Text",
      md: "Markdown",
      markdown: "Markdown",
      mdx: "MDX",

      // Spreadsheets
      xls: "Excel Spreadsheet",
      xlsx: "Excel Spreadsheet",
      csv: "CSV",
      ods: "OpenDocument Spreadsheet",

      // Presentations
      ppt: "PowerPoint Presentation",
      pptx: "PowerPoint Presentation",
      odp: "OpenDocument Presentation",

      // Images
      jpg: "JPEG Image",
      jpeg: "JPEG Image",
      png: "PNG Image",
      gif: "GIF Image",
      bmp: "Bitmap Image",
      svg: "SVG Image",
      webp: "WebP Image",
      tiff: "TIFF Image",
      ico: "Icon",

      // Archives
      zip: "ZIP Archive",
      rar: "RAR Archive",
      "7z": "7-Zip Archive",
      tar: "TAR Archive",
      gz: "Gzip Archive",

      // Code
      js: "JavaScript",
      ts: "TypeScript",
      jsx: "React JavaScript",
      tsx: "React TypeScript",
      py: "Python",
      java: "Java",
      cpp: "C++",
      c: "C",
      cs: "C#",
      rb: "Ruby",
      go: "Go",
      rs: "Rust",
      php: "PHP",
      html: "HTML",
      css: "CSS",
      scss: "SCSS",
      json: "JSON",
      xml: "XML",
      yaml: "YAML",
      yml: "YAML",

      // Audio
      mp3: "MP3 Audio",
      wav: "WAV Audio",
      ogg: "OGG Audio",
      aac: "AAC Audio",
      flac: "FLAC Audio",

      // Video
      mp4: "MP4 Video",
      mpeg: "MPEG Video",
      mov: "MOV Video",
      avi: "AVI Video",
      mkv: "MKV Video",
      webm: "WebM Video",
    };

    return extensionMap[extension] || null;
  };

  // If MIME type is generic/unknown and we have a filename, use the filename
  if ((!mimeType || mimeType === "application/octet-stream") && filename) {
    const typeFromFilename = getTypeFromFilename(filename);
    if (typeFromFilename) return typeFromFilename;
  }

  if (!mimeType) return "Unknown";

  const mimeTypeMap: Record<string, string> = {
    // Documents
    "application/pdf": "PDF Document",
    "application/msword": "Word Document",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Document",
    "text/plain": "Text Document",
    "application/rtf": "Rich Text Document",
    "application/vnd.oasis.opendocument.text": "OpenDocument Text",

    // Spreadsheets
    "application/vnd.ms-excel": "Excel Spreadsheet",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel Spreadsheet",
    "text/csv": "CSV",
    "application/vnd.oasis.opendocument.spreadsheet": "OpenDocument Spreadsheet",

    // Presentations
    "application/vnd.ms-powerpoint": "PowerPoint Presentation",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint Presentation",
    "application/vnd.oasis.opendocument.presentation": "OpenDocument Presentation",

    // Images
    "image/jpeg": "JPEG Image",
    "image/png": "PNG Image",
    "image/gif": "GIF Image",
    "image/bmp": "Bitmap Image",
    "image/svg+xml": "SVG Image",
    "image/webp": "WebP Image",
    "image/tiff": "TIFF Image",
    "image/x-icon": "Icon",

    // Archives
    "application/zip": "ZIP Archive",
    "application/x-rar-compressed": "RAR Archive",
    "application/x-7z-compressed": "7-Zip Archive",
    "application/x-tar": "TAR Archive",
    "application/gzip": "Gzip Archive",

    // Code
    "application/javascript": "JavaScript",
    "text/javascript": "JavaScript",
    "application/typescript": "TypeScript",
    "text/typescript": "TypeScript",
    "text/x-python": "Python",
    "application/x-python-code": "Python",
    "text/x-java-source": "Java",
    "text/x-c": "C",
    "text/x-c++": "C++",
    "text/x-csharp": "C#",
    "text/x-ruby": "Ruby",
    "text/x-go": "Go",
    "text/x-rust": "Rust",
    "application/x-httpd-php": "PHP",
    "text/html": "HTML",
    "text/css": "CSS",
    "application/json": "JSON",
    "application/xml": "XML",
    "text/xml": "XML",
    "application/x-yaml": "YAML",
    "text/yaml": "YAML",
    "text/markdown": "Markdown",
    "text/x-markdown": "Markdown",
    "application/markdown": "Markdown",

    // Audio
    "audio/mpeg": "MP3 Audio",
    "audio/wav": "WAV Audio",
    "audio/ogg": "OGG Audio",
    "audio/aac": "AAC Audio",
    "audio/flac": "FLAC Audio",

    // Video
    "video/mp4": "MP4 Video",
    "video/mpeg": "MPEG Video",
    "video/quicktime": "MOV Video",
    "video/x-msvideo": "AVI Video",
    "video/x-matroska": "MKV Video",
    "video/webm": "WebM Video",
  };

  // Check for exact match first
  if (mimeTypeMap[mimeType]) {
    return mimeTypeMap[mimeType];
  }

  // Fallback to generic type based on category
  const [category] = mimeType.split("/");

  switch (category) {
    case "text":
      return "Text";
    case "image":
      return "Image";
    case "audio":
      return "Audio";
    case "video":
      return "Video";
    case "application":
      return "Document";
    default:
      return "Unknown";
  }
}
